const STORAGE_PREFIX = 'lsgo:combinedReportAiJob:';

/** Fired after enqueue so the platform-wide watcher starts polling immediately. */
export const COMBINED_REPORT_AI_JOB_QUEUED = 'lsgo:combined-report-ai-job-queued';
/** Fired when a watched job reaches completed/failed (ReportHeader can refresh UI). */
export const COMBINED_REPORT_AI_JOB_FINISHED = 'lsgo:combined-report-ai-job-finished';

export type PendingCombinedReportAiJob = {
    jobId?: string;
    messageId?: string;
    schoolId: number;
    reportId: number;
    startedAt: number;
};

function storageKey(schoolId: number | string, reportId: number | string): string {
    return `${STORAGE_PREFIX}${schoolId}:${reportId}`;
}

export function pendingWatchKey(
    pending: Pick<PendingCombinedReportAiJob, 'jobId' | 'messageId' | 'schoolId' | 'reportId'>,
): string {
    return pending.jobId || pending.messageId || `${pending.schoolId}:${pending.reportId}`;
}

function isWatchable(parsed: PendingCombinedReportAiJob): boolean {
    return Boolean(parsed?.jobId || parsed?.messageId);
}

export function savePendingCombinedReportAiJob(
    schoolId: number | string,
    reportId: number | string,
    jobOrIds: string | { jobId?: string; messageId?: string },
): void {
    try {
        const ids = typeof jobOrIds === 'string' ? { jobId: jobOrIds } : jobOrIds;
        const payload: PendingCombinedReportAiJob = {
            ...ids,
            schoolId: +schoolId,
            reportId: +reportId,
            startedAt: Date.now(),
        };
        sessionStorage.setItem(storageKey(schoolId, reportId), JSON.stringify(payload));
        window.dispatchEvent(
            new CustomEvent(COMBINED_REPORT_AI_JOB_QUEUED, {
                detail: payload,
            }),
        );
    } catch {
        /* private mode / quota — non-blocking */
    }
}

/** All in-flight combined AI jobs (sessionStorage), skipping stale entries. */
export function listPendingCombinedReportAiJobs(): PendingCombinedReportAiJob[] {
    const out: PendingCombinedReportAiJob[] = [];
    try {
        for (let i = 0; i < sessionStorage.length; i += 1) {
            const key = sessionStorage.key(i);
            if (!key || !key.startsWith(STORAGE_PREFIX)) continue;
            const raw = sessionStorage.getItem(key);
            if (!raw) continue;
            try {
                const parsed = JSON.parse(raw) as PendingCombinedReportAiJob;
                if (!isWatchable(parsed)) continue;
                if (parsed.startedAt && Date.now() - parsed.startedAt > 30 * 60 * 1000) {
                    sessionStorage.removeItem(key);
                    continue;
                }
                out.push(parsed);
            } catch {
                /* skip bad row */
            }
        }
    } catch {
        /* ignore */
    }
    return out;
}

export function clearPendingCombinedReportAiJob(
    schoolId: number | string | null | undefined,
    reportId: number | string | null | undefined,
): void {
    if (schoolId == null || reportId == null) return;
    try {
        sessionStorage.removeItem(storageKey(schoolId, reportId));
    } catch {
        /* ignore */
    }
}

export function readPendingCombinedReportAiJob(
    schoolId: number | string | null | undefined,
    reportId: number | string | null | undefined,
): PendingCombinedReportAiJob | null {
    if (schoolId == null || reportId == null) return null;
    try {
        const raw = sessionStorage.getItem(storageKey(schoolId, reportId));
        if (!raw) return null;
        const parsed = JSON.parse(raw) as PendingCombinedReportAiJob;
        if (!isWatchable(parsed)) return null;
        // Drop stale jobs older than 30 minutes.
        if (parsed.startedAt && Date.now() - parsed.startedAt > 30 * 60 * 1000) {
            clearPendingCombinedReportAiJob(schoolId, reportId);
            return null;
        }
        return parsed;
    } catch {
        return null;
    }
}
